import React, { useState, useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/EditArticle.css';

const EditArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    category: '',
    featured: false,
    source: '',
    author: '',
    altTag:'',
    metaTitle:'',
    metaDescription:'',
    country:'',
  });
  const [content, setContent] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const backend_api = process.env.REACT_APP_API_BASE_URL;

  // List of categories
  const categories = [
    'Regulation & Policy', 'Business & Marketing', 'R&D', 'Ethics & Fairness',
    'Cybersecurity', 'Autonomous Systems', 'Healthcare', 'Sports',
    'Machine Learning', 'Generative AI', 'Education', 'Hardware & Infra',
    'Finance', 'Defense', 'General','AI News',
  ];

  useEffect(() => {
    if (!id) {
      alert('Invalid article ID.');
      navigate('/dashboard');
      return;
    }

    const fetchArticle = async () => {
      try {
        console.log('Fetching article with ID:', id);
        const articleRef = doc(db, 'articles', id);
        const articleSnap = await getDoc(articleRef);

        if (articleSnap.exists()) {
          const articleData = articleSnap.data();
          console.log('Fetched article:', articleData);
          setArticle(articleData);
          setFormData({
            title: articleData.title,
            summary: articleData.summary || '',
            category: articleData.category || 'Uncategorized',
            featured: articleData.featured || false,
            source: articleData.source || '',
            author: articleData.author || '',
            altTag: articleData.altTag || '',
            metaTitle: articleData.metaTitle || '',
            metaDescription: articleData.metaDescription || '',
            country: articleData.country || '',
          });

          setContent(articleData.content || '');
        } else {
          alert('Article not found.');
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        alert('Failed to fetch article.');
        navigate('/dashboard');
      }
    };

    fetchArticle();
  }, [id, navigate]);

  const handleSave = async () => {
    try {
      if (!id) {
        alert('Invalid article ID.');
        return;
      }

      const articleRef = doc(db, 'articles', id);
      await updateDoc(articleRef, {
        ...formData,
        content,
      });

      alert('Article updated successfully.');
    } catch (error) {
      console.error('Error updating article:', error);
      alert('Failed to update the article.');
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  // Handle image file selection and preview
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);

      const reader = new FileReader();
      reader.onload = (event) => {
        setPreviewImageUrl(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpdate = async () => {
    if (!selectedImage) {
      alert('Please select an image to upload.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('currentImageUrl', article?.imageUrl || '');

      console.log('Uploading Image:', selectedImage.name);

      const response = await fetch(`${backend_api}/api/image/update`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        const newImageUrl = data.imageUrl;
        const articleRef = doc(db, 'articles', id);
        await updateDoc(articleRef, { imageUrl: newImageUrl });

        setArticle((prevArticle) => ({
          ...prevArticle,
          imageUrl: newImageUrl,
        }));

        alert('Image updated successfully.');
        setSelectedImage(null);
        setPreviewImageUrl('');
      } else {
        throw new Error(data.message || 'Failed to update image.');
      }
    } catch (error) {
      console.error('Error updating image:', error);
      alert('Failed to update image.');
    }
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await fetch(`${backend_api}/api/image/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        return { default: data.imageUrl };
      } else {
        throw new Error(data.message || 'Failed to upload image.');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      return { default: '' };
    }
  };

  const CustomUploadAdapter = (loader) => ({
    upload: () => loader.file
      .then((file) => handleImageUpload(file))
      .then((response) => response),
  });

  if (!article) {
    return <div>Loading...</div>;
  }

  return (
    <div className="edit-article-page">
      <button className="back-button" onClick={handleBack}>
        &larr; Back to Dashboard
      </button>
      <h1>Edit News Article</h1>

      <div className="form-group">
        <label>Title</label>
        <input
          type="text"
          className="title-input"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Title"
        />
      </div>

      <div className="form-group">
        <label>Summary</label>
        <textarea
          className="summary-textarea"
          value={formData.summary}
          onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
          placeholder="Summary"
          rows="4"
        ></textarea>
      </div>

      <div className="form-group">
        <label>Category</label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          className="category-select-edit"
        >
          <option value="">Select a category</option>
          {categories.map((category) => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>
      <div className="form-group">
        <label>Source</label>
        <input
          type="text"
          value={formData.source}
          onChange={(e) => setFormData({ ...formData, source: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Author</label>
        <input
          type="text"
          value={formData.author}
          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
        />
      </div>
      <div className="form-group">
        <label>altTag</label>
        <input
          type="text"
          className="title-input"
          value={formData.altTag}
          onChange={(e) => setFormData({ ...formData, altTag: e.target.value })}
          placeholder="altTag"
        />
      </div>
      <div className="form-group">
        <label>Country</label>
        <input
          type="text"         
          value={formData.country}
          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
          placeholder="Country"
        />
      </div>
      <div className="form-group">
        <label>Meta title</label>
        <input
          type="text"
          className="title-input"
          value={formData.metaTitle}
          onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
          placeholder="Meta Title"
        />
      </div>
      <div className="form-group">
        <label>Meta Description</label>
        <textarea
          className="summary-textarea"
          value={formData.metaDescription}
          onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
          placeholder="Meta Description"
          rows="4"
        ></textarea>
      </div>
      <div className="form-group">
        <label>Content</label>
        <CKEditor
          editor={ClassicEditor}
          data={content}
          config={{
            extraPlugins: [function (editor) {
              editor.plugins.get('FileRepository').createUploadAdapter = (loader) => CustomUploadAdapter(loader);
            }]
          }}
          onChange={(event, editor) => setContent(editor.getData())}
        />
      </div>

      <div className="form-group">
        <label>Featured Image</label>
        {article.imageUrl && <img src={article.imageUrl} alt="Current Featured" />}
        {previewImageUrl && <img src={previewImageUrl} alt="Preview" />}
        <input type="file" accept="image/*" onChange={handleImageChange} />
        <button className="update-image-button" onClick={handleImageUpdate}>Update Featured Image</button>
      </div>

      <div className="button-group">
        <button className="save-button" onClick={handleSave}>Save</button>
        <button className="cancel-button" onClick={handleBack}>Cancel</button>
      </div>
    </div>
  );
};

export default EditArticle;
